import { useEffect, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import './InGameSettingsModal.css';
import { useModaleAccessibile } from '../../hooks/useModaleAccessibile';

/* Lazy: il guscio del modale e' minuscolo, il peso e' tutto qui dentro (col suo
   CSS e il ColorPickerOverlay). Statico, teneva il pannello nel preload
   d'ingresso dell'app anche per chi non apre mai le impostazioni in partita. */
const PreferencesPanel = lazy(() => import('../profile/PreferencesPanel'));

interface InGameSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

export default function InGameSettingsModal({ open, onClose }: InGameSettingsModalProps) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Close on a device back press.
  useOverlayBackButton(open, onClose);

  /*
   * Il fuoco dentro il dialogo, Tab che non ne esce, e alla chiusura il fuoco
   * torna a chi l'aveva: senza, la tabulazione continua nella pagina SOTTO, che
   * è ancora lì dietro il velo. Senza `onClose` perché l'Escape lo gestisce già
   * il componente. Vedi `useModaleAccessibile`.
   *
   * STA SOPRA il `return null`, e non è una questione di stile: sotto, il modale
   * chiuso chiamava un hook in meno di quello aperto, e all'apertura React
   * lanciava «Rendered more hooks than during the previous render» — cioè
   * l'intera app finiva sulla schermata d'errore appena si premeva
   * IMPOSTAZIONI. Per questo l'argomento è `open` e non `true`: è esattamente il
   * parametro `attivo` dell'hook, che a modale chiuso non fa niente.
   */
  const riferimentoDialogo = useModaleAccessibile<HTMLDivElement>(open);

  if (!open) return null;

  return (
    <div className="ingame-settings-overlay" onClick={onClose}>
      {/* `role`/`aria-modal` scendono di un livello, sul PANNELLO: erano sul velo,
          che è il fondo cliccabile, non la finestra. Un dialogo il cui nodo è il
          velo dichiara come proprio contenuto anche il fondo. */}
      <div
        ref={riferimentoDialogo}
        className="ingame-settings-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ingame-settings-titolo"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ingame-settings-header">
          <h3 className="settings-title" id="ingame-settings-titolo">{t('profile.settings', 'Settings')}</h3>
          <button
            type="button"
            className="ingame-settings-close"
            onClick={onClose}
            aria-label={t('common.close', 'Close')}
          >
            <X size={20} />
          </button>
        </div>
        <div className="ingame-settings-body">
          <Suspense fallback={null}>
            <PreferencesPanel />
          </Suspense>
        </div>
      </div>
    </div>
  );
}
